import React, { useRef, useMemo } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, Text } from '@react-three/drei';
import * as THREE from 'three';

const Asteroid = () => {
  const meshRef = useRef<THREE.Mesh>(null);
  
  // Create jagged geometry once
  const geometry = useMemo(() => {
    const geo = new THREE.IcosahedronGeometry(1.2, 1);
    const posAttribute = geo.attributes.position;
    const vertex = new THREE.Vector3();
    for (let i = 0; i < posAttribute.count; i++) {
      vertex.fromBufferAttribute(posAttribute, i);
      // Displace
      vertex.normalize().multiplyScalar(1.2 + Math.random() * 0.3);
      posAttribute.setXYZ(i, vertex.x, vertex.y, vertex.z);
    }
    geo.computeVertexNormals();
    return geo;
  }, []);

  useFrame((state, delta) => {
    if (meshRef.current) {
      meshRef.current.rotation.x += delta * 0.2;
      meshRef.current.rotation.y += delta * 0.3;
    }
  });

  return (
    <group position={[-2, 0, 0]}>
      <mesh ref={meshRef} geometry={geometry}>
        <meshStandardMaterial color="#8B7355" roughness={0.9} metalness={0.1} />
      </mesh>
      <Text position={[0, -2.5, 0]} fontSize={0.4} color="white">
        Asteroid
      </Text>
    </group>
  );
};

const Comet = () => {
  const meshRef = useRef<THREE.Mesh>(null);
  const particlesRef = useRef<THREE.Points>(null);

  // Create comet tail particles
  const particlesGeo = useMemo(() => {
    const geo = new THREE.BufferGeometry();
    const count = 200;
    const positions = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      // Trail to the right (+x)
      positions[i * 3] = Math.random() * 5; // x
      positions[i * 3 + 1] = (Math.random() - 0.5) * 1.5; // y
      positions[i * 3 + 2] = (Math.random() - 0.5) * 1.5; // z
    }
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    return geo;
  }, []);

  useFrame((state, delta) => {
    if (meshRef.current) {
      meshRef.current.rotation.y -= delta * 0.5;
    }
    if (particlesRef.current) {
      // Animate particles flowing to the right
      const positions = particlesRef.current.geometry.attributes.position.array as Float32Array;
      for (let i = 0; i < 200; i++) {
        positions[i * 3] += delta * 2; // Move right
        if (positions[i * 3] > 5) {
          positions[i * 3] = 0; // Reset to comet head
          positions[i * 3 + 1] = (Math.random() - 0.5) * 0.5;
          positions[i * 3 + 2] = (Math.random() - 0.5) * 0.5;
        }
      }
      particlesRef.current.geometry.attributes.position.needsUpdate = true;
    }
  });

  return (
    <group position={[2, 0, 0]}>
      <mesh ref={meshRef}>
        <sphereGeometry args={[0.7, 32, 32]} />
        <meshStandardMaterial color="#B0C8E0" emissive="#4488FF" emissiveIntensity={0.3} />
      </mesh>
      <points ref={particlesRef} geometry={particlesGeo}>
        <pointsMaterial color="#88CCFF" size={0.1} transparent opacity={0.6} sizeAttenuation={true} />
      </points>
      <Text position={[0, -2.5, 0]} fontSize={0.4} color="white">
        Comet
      </Text>
    </group>
  );
};

export const AsteroidComet: React.FC<{ width?: number | string; height?: number | string }> = ({ width = '100%', height = 400 }) => {
  return (
    <div style={{ width, height, background: '#0F172A', borderRadius: '12px', overflow: 'hidden' }}>
      <Canvas camera={{ position: [0, 0, 7], fov: 45 }}>
        <ambientLight intensity={0.4} />
        <directionalLight position={[5, 5, 5]} intensity={1} />
        <Asteroid />
        <Comet />
        <OrbitControls enableZoom={true} enablePan={false} />
      </Canvas>
    </div>
  );
};
